import React, { createContext, useContext, useEffect, useState } from "react";
import { translations, RTL_LANGS } from "../i18n/translations";

const AppContext = createContext(null);

// AMB Élite propose deux moods pour le même site :
//   • "winter" (nuit alpine, mode sombre) — PAR DÉFAUT — sombre, dramatique, prestige
//   • "summer" (ivoire maison) — clair, luxe, boutique (sur bascule utilisateur)
// Le choix est persisté dans localStorage ("amb_theme") pour que le visiteur
// retrouve son mood à sa prochaine visite.
const THEME_KEY = "amb_theme";

const getDefaultSeason = () => {
  try {
    const stored = localStorage.getItem(THEME_KEY);
    if (stored === "winter" || stored === "summer") return stored;
  } catch (e) {
    // localStorage can throw in private mode — fall back to winter silently.
  }
  return "winter";
};

const getDefaultLang = () => {
  // AMB Élite est une maison VTC française : le français est la langue
  // principale et par défaut. L'anglais ne s'applique que si l'utilisateur
  // bascule explicitement via le switcher globe.
  try {
    const stored = localStorage.getItem("amb_lang");
    if (stored && translations[stored]) return stored;
  } catch (e) {
    // localStorage can throw in private mode — fall back to fr silently.
  }
  return "fr";
};

export const AppProvider = ({ children }) => {
  const [season, setSeason] = useState(getDefaultSeason);
  const [lang, setLangState] = useState(getDefaultLang);

  const setLang = (l) => {
    localStorage.setItem("amb_lang", l);
    setLangState(l);
  };

  const toggleSeason = () =>
    setSeason((s) => {
      const next = s === "winter" ? "summer" : "winter";
      try {
        localStorage.setItem(THEME_KEY, next);
      } catch (e) {
        // ignore storage errors (private browsing)
      }
      return next;
    });

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove("theme-winter", "theme-summer");
    root.classList.add(`theme-${season}`);
  }, [season]);

  // Apply text direction (RTL for Arabic) on the <html> element.
  useEffect(() => {
    document.documentElement.setAttribute(
      "dir",
      RTL_LANGS.includes(lang) ? "rtl" : "ltr"
    );
  }, [lang]);

  const value = {
    season,
    isWinter: season === "winter",
    isDark: season === "winter",
    toggleSeason,
    toggleTheme: toggleSeason, // alias plus explicite pour les nouveaux composants
    lang,
    setLang,
    isRtl: RTL_LANGS.includes(lang),
    t: translations[lang] || translations.fr, // garde-fou : jamais undefined
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = () => useContext(AppContext);
